// ==================== CUSTOMERS PAGE (ADMIN) ====================

import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Filter, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Building2,
  Mail,
  Calendar,
  Eye,
  UserCheck,
  UserX,
  ChevronDown,
  Users,
  AlertCircle,
  RefreshCw,
  Ban,
  Loader2,
  Phone,
  CreditCard,
  FileText,
  Send,
  MapPin,
  Info,
  CalendarX,
  Target
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';
import { Pagination } from '../../components/ui/Pagination';
import { Dropdown, type DropdownItem } from '../../components/ui/Dropdown';
import { Modal, ModalBody } from '../../components/ui/Modal';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { cn } from '../../utils/helpers';
import { fadeInUp, staggerContainer } from '../../utils/animations';
import { customersService, type CustomerOrganization, type CustomerStatistics, type AssignPlanData } from '../../services';
import { leadGenAddonCycleAmount, type LeadGenAddonQuote } from '../../services/subscription.service';
import { plansService } from '../../services';
import type { SubscriptionPlan } from '../../types';
import { APP_CONSTANTS } from '../../constants';

// ==================== TYPES ====================

type FilterStatus = 'all' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'SUSPENDED';

type CustomerSubscription = NonNullable<CustomerOrganization['subscription']>;

// Mirrors the backend rule: add-ons can only be managed while the plan grants access.
const hasPlanAccess = (subscription: CustomerSubscription) =>
  subscription.status === 'ACTIVE' ||
  (subscription.status === 'CANCELLED' && new Date(subscription.currentPeriodEnd) > new Date());

// Card customers on auto-renew manage the add-on themselves (it would be charged to their card).
const isCardAutoRenew = (subscription: CustomerSubscription) =>
  !!(subscription.autoRenew && subscription.stripeCustomerId && subscription.defaultPaymentMethodId);

// ==================== STAT CARD COMPONENT ====================

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  color: string;
  isLoading?: boolean;
}

const StatCard = ({ title, value, icon: Icon, color, isLoading }: StatCardProps) => (
  <Card className="flex-1">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-(--color-muted-gray)">{title}</p>
        {isLoading ? (
          <div className="h-8 w-16 bg-(--color-border-gray) rounded animate-pulse mt-1" />
        ) : (
          <p className="text-2xl font-bold text-white font-heading">{value}</p>
        )}
      </div>
      <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', color)}>
        <Icon size={20} className="text-white" />
      </div>
    </div>
  </Card>
);

// ==================== ACTION DROPDOWN COMPONENT ====================

interface ActionDropdownProps {
  customer: CustomerOrganization;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onSuspend: (id: string) => void;
  onReactivate: (id: string) => void;
  onView: (id: string) => void;
  onAssignPlan: (customer: CustomerOrganization) => void;
  onExpirePlan: (customer: CustomerOrganization) => void;
  onEnableLeadGen: (customer: CustomerOrganization) => void;
  onDisableLeadGen: (customer: CustomerOrganization) => void;
}

const ActionDropdown = ({ customer, onApprove, onReject, onSuspend, onReactivate, onView, onAssignPlan, onExpirePlan, onEnableLeadGen, onDisableLeadGen }: ActionDropdownProps) => {
  const subscription = customer.subscription;
  const canManageAddon = !!subscription && hasPlanAccess(subscription);

  const items: DropdownItem[] = [
    {
      key: 'view',
      label: 'View Details',
      icon: <Eye size={16} />,
      onClick: () => onView(customer.id),
    },
    {
      key: 'assign-plan',
      label: 'Assign Plan',
      icon: <CreditCard size={16} />,
      colorClass: 'text-(--color-signal-orange)',
      visible: customer.status === 'APPROVED',
      onClick: () => onAssignPlan(customer),
    },
    {
      key: 'expire-plan',
      label: 'Expire Plan',
      icon: <CalendarX size={16} />,
      colorClass: 'text-(--color-warning)',
      // Only meaningful when there is a live plan to expire.
      visible: !!customer.subscription && customer.subscription.status !== 'EXPIRED',
      onClick: () => onExpirePlan(customer),
    },
    {
      key: 'enable-lead-gen',
      label: 'Enable Lead Gen Add-on',
      icon: <Target size={16} />,
      colorClass: 'text-(--color-signal-orange)',
      visible:
        canManageAddon &&
        !subscription.leadGenAddonEnabled &&
        !isCardAutoRenew(subscription) &&
        Number(subscription.plan.leadGenAddonFee ?? 0) > 0,
      onClick: () => onEnableLeadGen(customer),
    },
    {
      key: 'disable-lead-gen',
      label: 'Disable Lead Gen Add-on',
      icon: <Target size={16} />,
      colorClass: 'text-(--color-warning)',
      visible: canManageAddon && !!subscription.leadGenAddonEnabled,
      onClick: () => onDisableLeadGen(customer),
    },
    {
      key: 'approve',
      label: 'Approve',
      icon: <UserCheck size={16} />,
      colorClass: 'text-(--color-success)',
      visible: customer.status === 'PENDING' && customer.user.isActive,
      onClick: () => onApprove(customer.id),
    },
    {
      key: 'reject',
      label: 'Reject',
      icon: <UserX size={16} />,
      colorClass: 'text-(--color-warning)',
      visible: customer.status === 'PENDING' && customer.user.isActive,
      onClick: () => onReject(customer.id),
    },
    {
      key: 'suspend',
      label: 'Suspend',
      icon: <Ban size={16} />,
      colorClass: 'text-(--color-warning)',
      visible: customer.status === 'APPROVED',
      onClick: () => onSuspend(customer.id),
    },
    {
      key: 'reactivate',
      label: 'Reactivate',
      icon: <RefreshCw size={16} />,
      colorClass: 'text-(--color-success)',
      visible: customer.status === 'SUSPENDED',
      onClick: () => onReactivate(customer.id),
    },
  ];

  return <Dropdown items={items} menuWidth={200} />;
};

// ==================== SUBSCRIPTION BADGE COMPONENT ====================

/**
 * Shows the plan name plus its subscription status. Previously the table only
 * rendered the plan name, so an expired plan was indistinguishable from a live one.
 */
const SubscriptionBadge = ({
  subscription,
}: {
  subscription?: CustomerOrganization['subscription'];
}) => {
  if (!subscription) {
    return <span className="text-xs text-(--color-muted-gray) italic">No plan</span>;
  }

  const status = subscription.status?.toUpperCase();

  const config: Record<
    string,
    { variant: 'default' | 'orange' | 'success' | 'error' | 'warning' | 'info'; label: string; icon: ReactNode }
  > = {
    ACTIVE: { variant: 'success', label: 'Active', icon: <CheckCircle size={11} className="mr-1" /> },
    PENDING: { variant: 'warning', label: 'Awaiting payment', icon: <Clock size={11} className="mr-1" /> },
    PAST_DUE: { variant: 'warning', label: 'Past due', icon: <AlertCircle size={11} className="mr-1" /> },
    CANCELLED: { variant: 'default', label: 'Cancelled', icon: <Ban size={11} className="mr-1" /> },
    EXPIRED: { variant: 'error', label: 'Expired', icon: <CalendarX size={11} className="mr-1" /> },
  };

  const state = config[status] ?? {
    variant: 'default' as const,
    label: subscription.status || 'Unknown',
    icon: null,
  };

  // An expired or cancelled plan grants no access, so mute the plan name to keep
  // the live status the thing that reads first.
  const isInactive = status === 'EXPIRED' || status === 'CANCELLED';

  return (
    <div className="flex flex-col items-start gap-1">
      <Badge variant={isInactive ? 'default' : 'orange'} size="sm">
        {subscription.plan.name}
      </Badge>
      <Badge variant={state.variant} size="sm">
        {state.icon}
        {state.label}
      </Badge>
      {subscription.leadGenAddonEnabled && !isInactive && (
        <Badge variant="info" size="sm">
          <Target size={11} className="mr-1" />
          Lead Gen
        </Badge>
      )}
    </div>
  );
};


// ==================== STATUS BADGE COMPONENT ====================

const StatusBadge = ({ status, isUserActive }: { status: CustomerOrganization['status']; isUserActive?: boolean }) => {
  // If organization is approved but user account is not active (email not verified)
  if (status === 'APPROVED' && isUserActive === false) {
    return (
      <Badge variant="warning">
        <Clock size={12} className="mr-1" />
        Email Not Verified
      </Badge>
    );
  }

  switch (status) {
    case 'APPROVED':
      return (
        <Badge variant="success">
          <CheckCircle size={12} className="mr-1" />
          Active
        </Badge>
      );
    case 'PENDING':
      return (
        <Badge variant="warning">
          <Clock size={12} className="mr-1" />
          Pending
        </Badge>
      );
    case 'REJECTED':
      return (
        <Badge variant="error">
          <XCircle size={12} className="mr-1" />
          Rejected
        </Badge>
      );
    case 'SUSPENDED':
      return (
        <Badge variant="default">
          <Ban size={12} className="mr-1" />
          Suspended
        </Badge>
      );
    default:
      return null;
  }
};

// ==================== MODAL HELPER COMPONENTS ====================

interface InfoRowProps {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  value: React.ReactNode;
}

const InfoRow = ({ icon: Icon, label, value }: InfoRowProps) => (
  <div className="flex items-start gap-3 py-3 border-b border-(--color-border-gray)/30 last:border-b-0">
    <div className="w-8 h-8 rounded-lg bg-(--color-navy-light) flex items-center justify-center shrink-0">
      <Icon size={16} className="text-(--color-signal-orange)" />
    </div>
    <div className="flex-1 min-w-0 overflow-hidden">
      <p className="text-xs text-(--color-muted-gray) mb-0.5">{label}</p>
      <div className="text-sm text-white truncate" title={typeof value === 'string' ? value : undefined}>
        {value || '—'}
      </div>
    </div>
  </div>
);

interface StatItemProps {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  value: number;
}

const StatItem = ({ icon: Icon, label, value }: StatItemProps) => (
  <div className="flex flex-col items-center p-3 rounded-lg bg-(--color-navy-light)/50">
    <Icon size={18} className="text-(--color-signal-orange) mb-1" />
    <p className="text-xl font-bold text-white">{value}</p>
    <p className="text-xs text-(--color-muted-gray)">{label}</p>
  </div>
);

// ==================== CUSTOMER DETAILS MODAL ====================

interface CustomerDetailsModalProps {
  customer: CustomerOrganization | null;
  isOpen: boolean;
  onClose: () => void;
}

const formatModalDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const CustomerDetailsModal = ({ customer, isOpen, onClose }: CustomerDetailsModalProps) => {
  if (!customer) return null;

  // Custom header with avatar and status
  const customHeader = (
    <div className="flex items-center gap-4 flex-1">
      <div className="w-12 h-12 rounded-full bg-linear-to-br from-(--color-signal-orange) to-(--color-warm-amber) flex items-center justify-center shadow-lg shadow-(--color-signal-orange)/20">
        <span className="text-lg font-bold text-white">
          {customer.user.firstName[0]}{customer.user.lastName[0]}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <h2 className="text-lg font-semibold text-white">
          {customer.user.firstName} {customer.user.lastName}
        </h2>
        <p className="text-sm text-(--color-muted-gray)">{customer.businessName}</p>
      </div>
      <StatusBadge status={customer.status} isUserActive={customer.user.isActive} />
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      customHeader={customHeader}
    >
      <ModalBody>
        {/* Stats Grid */}
        {customer._count && (
          <div className="grid grid-cols-3 gap-3 mb-6">
            <StatItem icon={Users} label="Contacts" value={customer._count.contacts} />
            <StatItem icon={Send} label="Campaigns" value={customer._count.campaigns} />
            <StatItem icon={FileText} label="Templates" value={customer._count.templates} />
          </div>
        )}

        {/* Two Column Layout */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Contact Information */}
          <div>
            <h3 className="text-sm font-semibold text-(--color-soft-gray) uppercase tracking-wider mb-3">
              Contact Information
            </h3>
            <div className="bg-(--color-navy-light)/30 rounded-xl p-4">
              <InfoRow icon={Mail} label="Email" value={customer.user.email} />
              <InfoRow icon={Phone} label="Business Phone" value={customer.businessPhone} />
              <InfoRow icon={MapPin} label="Address" value={customer.businessAddress} />
            </div>
          </div>

          {/* Business Details */}
          <div>
            <h3 className="text-sm font-semibold text-(--color-soft-gray) uppercase tracking-wider mb-3">
              Business Details
            </h3>
            <div className="bg-(--color-navy-light)/30 rounded-xl p-4">
              <InfoRow icon={Building2} label="Business Name" value={customer.businessName} />
              <InfoRow icon={Mail} label="Business Email" value={customer.businessEmail} />
              <InfoRow 
                icon={CreditCard} 
                label="Subscription" 
                value={<SubscriptionBadge subscription={customer.subscription} />} 
              />
            </div>
          </div>
        </div>

        {/* Account Status & Dates */}
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-(--color-soft-gray) uppercase tracking-wider mb-3">
            Account Timeline
          </h3>
          <div className="bg-(--color-navy-light)/30 rounded-xl p-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-(--color-info)"></div>
                <div>
                  <p className="text-xs text-(--color-muted-gray)">Registered</p>
                  <p className="text-sm text-white">{formatModalDate(customer.createdAt)}</p>
                </div>
              </div>
              {customer.approvedAt && (
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-(--color-success)"></div>
                  <div>
                    <p className="text-xs text-(--color-muted-gray)">Approved</p>
                    <p className="text-sm text-white">{formatModalDate(customer.approvedAt)}</p>
                  </div>
                </div>
              )}
              {customer.rejectedAt && (
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-(--color-error)"></div>
                  <div>
                    <p className="text-xs text-(--color-muted-gray)">Rejected</p>
                    <p className="text-sm text-white">{formatModalDate(customer.rejectedAt)}</p>
                  </div>
                </div>
              )}
              {customer.user.lastLoginAt && (
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-(--color-signal-orange)"></div>
                  <div>
                    <p className="text-xs text-(--color-muted-gray)">Last Login</p>
                    <p className="text-sm text-white">{formatModalDate(customer.user.lastLoginAt)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Rejection Reason */}
        {customer.rejectionReason && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-(--color-soft-gray) uppercase tracking-wider mb-3">
              {customer.status === 'SUSPENDED' ? 'Suspension Reason' : 'Rejection Reason'}
            </h3>
            <div className="bg-(--color-error)/10 border border-(--color-error)/30 rounded-xl p-4">
              <p className="text-sm text-(--color-soft-gray)">{customer.rejectionReason}</p>
            </div>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

// ==================== ASSIGN PLAN MODAL ====================

interface AssignPlanModalProps {
  customer: CustomerOrganization | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const PAYMENT_MODES = [
  { value: 'CASH', label: 'Cash' },
  { value: 'CHECK', label: 'Cheque' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'OTHER', label: 'Other' },
] as const;

const AssignPlanModal = ({ customer, isOpen, onClose, onSuccess }: AssignPlanModalProps) => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [billingCycle, setBillingCycle] = useState<'MONTHLY' | 'YEARLY'>('MONTHLY');
  const [paymentMode, setPaymentMode] = useState<AssignPlanData['paymentMode']>('CASH');
  const [paymentReference, setPaymentReference] = useState('');
  const [notes, setNotes] = useState('');
  const [includeSetupFee, setIncludeSetupFee] = useState(false);
  const [includeLeadGenAddon, setIncludeLeadGenAddon] = useState(false);

  const selectedPlan = plans.find(p => p.id === selectedPlanId);

  const planAmount = selectedPlan
    ? billingCycle === 'YEARLY' && selectedPlan.yearlyPrice
      ? Number(selectedPlan.yearlyPrice)
      : Number(selectedPlan.monthlyPrice)
    : 0;
  const setupFeeAmount = includeSetupFee && selectedPlan ? Number(selectedPlan.setupFee || 0) : 0;
  // Monthly fee billed for the whole cycle (12 months on yearly).
  const leadGenAddonAmount = includeLeadGenAddon && selectedPlan
    ? leadGenAddonCycleAmount(Number(selectedPlan.leadGenAddonFee || 0), billingCycle === 'YEARLY')
    : 0;
  const totalAmount = planAmount + setupFeeAmount + leadGenAddonAmount;

  useEffect(() => {
    if (isOpen) {
      setLoadingPlans(true);
      plansService.getPlans()
        .then(data => setPlans(data.filter(p => p.isActive !== false)))
        .catch(() => setModalError('Failed to load plans'))
        .finally(() => setLoadingPlans(false));
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedPlanId('');
      setBillingCycle('MONTHLY');
      setPaymentMode('CASH');
      setPaymentReference('');
      setNotes('');
      setIncludeSetupFee(false);
      setIncludeLeadGenAddon(false);
      setModalError(null);
    }
  }, [isOpen]);

  // When customer has existing subscription, pre-select current plan
  useEffect(() => {
    if (isOpen && customer?.subscription?.plan && plans.length > 0) {
      const currentPlanId = customer.subscription.plan.id;
      if (plans.some(p => p.id === currentPlanId)) {
        setSelectedPlanId(currentPlanId);
      }
    }
  }, [isOpen, customer?.subscription?.plan?.id, plans]);

  const handleSave = async () => {
    if (!customer || !selectedPlanId) return;

    try {
      setSaving(true);
      setModalError(null);

      const data: AssignPlanData = {
        planId: selectedPlanId,
        billingCycle,
        paymentMode,
        ...(paymentReference.trim() && { paymentReference: paymentReference.trim() }),
        ...(notes.trim() && { notes: notes.trim() }),
        includeSetupFee,
        includeLeadGenAddon,
      };

      const response = await customersService.assignPlan(customer.id, data);
      if (response.success) {
        onClose();
        onSuccess();
      } else {
        setModalError(response.message || 'Failed to assign plan');
      }
    } catch (err: unknown) {
      setModalError(err instanceof Error ? err.message : 'Failed to assign plan');
    } finally {
      setSaving(false);
    }
  };

  const isFormValid = !!selectedPlanId;

  const periodEnd = new Date();
  if (billingCycle === 'YEARLY') {
    periodEnd.setFullYear(periodEnd.getFullYear() + 1);
  } else {
    periodEnd.setMonth(periodEnd.getMonth() + 1);
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Assign Subscription Plan"
      subtitle={customer ? `${customer.user.firstName} ${customer.user.lastName} — ${customer.businessName}` : ''}
      size="lg"
      showCloseButton
    >
      <div className="space-y-5 p-6">
        {loadingPlans ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-(--color-signal-orange)" />
          </div>
        ) : (
          <>
            {/* Existing subscription notice */}
            {customer?.subscription && ['ACTIVE', 'PENDING', 'PAST_DUE'].includes(customer.subscription.status) && (
              <div className="flex items-start gap-3 p-4 rounded-lg bg-(--color-info)/10 border border-(--color-info)/30">
                <Info className="w-5 h-5 text-(--color-info) shrink-0 mt-0.5" />
                <div className="text-sm text-(--color-soft-gray)">
                  <p className="font-medium text-white mb-1">This customer already has an active plan</p>
                  <p>
                    Current plan: <span className="text-(--color-signal-orange) font-medium">{customer.subscription.plan.name}</span>.
                    Assigning a plan here will replace it and start a new billing period from today. Auto-renew will be turned off so you can manage renewals manually (e.g. when they pay by cash or cheque).
                  </p>
                </div>
              </div>
            )}

            {/* Plan Selection */}
            <div>
              <label className="block text-sm font-medium text-(--color-soft-gray) mb-2">
                Select Plan
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {plans.map(plan => (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => setSelectedPlanId(plan.id)}
                    className={cn(
                      'p-4 rounded-lg border text-left transition-all',
                      selectedPlanId === plan.id
                        ? 'border-(--color-signal-orange) bg-(--color-signal-orange)/10 ring-1 ring-(--color-signal-orange)/50'
                        : 'border-(--color-border-gray) bg-(--color-charcoal-black) hover:border-(--color-muted-gray)',
                    )}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-semibold text-white">{plan.name}</p>
                      {plan.isPopular && (
                        <Badge variant="orange" size="sm">Popular</Badge>
                      )}
                    </div>
                    <p className="text-xs text-(--color-muted-gray)">
                      ${Number(plan.monthlyPrice)}/mo
                      {plan.yearlyPrice && ` · $${Number(plan.yearlyPrice)}/yr`}
                    </p>
                    <p className="text-xs text-(--color-muted-gray) mt-1">
                      {plan.maxContacts.toLocaleString()} contacts · {plan.maxMessagesPerMonth.toLocaleString()} msg/mo
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Billing Cycle */}
            <div>
              <label className="block text-sm font-medium text-(--color-soft-gray) mb-2">
                Billing Cycle
              </label>
              <div className="flex gap-3">
                {(['MONTHLY', 'YEARLY'] as const).map(cycle => (
                  <button
                    key={cycle}
                    type="button"
                    onClick={() => setBillingCycle(cycle)}
                    className={cn(
                      'flex-1 py-2.5 px-4 rounded-lg border text-sm font-medium transition-all',
                      billingCycle === cycle
                        ? 'border-(--color-signal-orange) bg-(--color-signal-orange)/10 text-(--color-signal-orange)'
                        : 'border-(--color-border-gray) bg-(--color-charcoal-black) text-(--color-soft-gray) hover:border-(--color-muted-gray)',
                    )}
                  >
                    {cycle === 'MONTHLY' ? 'Monthly' : 'Yearly'}
                  </button>
                ))}
              </div>
            </div>

            {/* Payment Mode */}
            <div>
              <label className="block text-sm font-medium text-(--color-soft-gray) mb-2">
                Payment Method
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {PAYMENT_MODES.map(mode => (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() => setPaymentMode(mode.value)}
                    className={cn(
                      'py-2 px-3 rounded-lg border text-sm transition-all',
                      paymentMode === mode.value
                        ? 'border-(--color-signal-orange) bg-(--color-signal-orange)/10 text-(--color-signal-orange)'
                        : 'border-(--color-border-gray) bg-(--color-charcoal-black) text-(--color-soft-gray) hover:border-(--color-muted-gray)',
                    )}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Payment Reference */}
            <Input
              label="Payment Reference (optional)"
              placeholder="e.g., Cheque #1234, Receipt #5678"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
            />

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-(--color-soft-gray) mb-2">
                Notes (optional)
              </label>
              <textarea
                placeholder="Additional notes about this payment..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full px-4 py-3 rounded-lg bg-(--color-charcoal-black) border border-(--color-border-gray) text-white placeholder:text-(--color-muted-gray) focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 transition-all resize-none text-sm"
              />
            </div>

            {/* Include Setup Fee */}
            {selectedPlan && Number(selectedPlan.setupFee || 0) > 0 && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                <div>
                  <p className="text-sm text-white">Include Setup Fee</p>
                  <p className="text-xs text-(--color-muted-gray)">
                    ${Number(selectedPlan.setupFee || 0)} one-time fee
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIncludeSetupFee(!includeSetupFee)}
                  className={cn(
                    'relative w-11 h-6 rounded-full transition-colors',
                    includeSetupFee ? 'bg-(--color-signal-orange)' : 'bg-(--color-border-gray)',
                  )}
                >
                  <span
                    className={cn(
                      'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform',
                      includeSetupFee && 'translate-x-5',
                    )}
                  />
                </button>
              </div>
            )}

            {/* Include Lead Generation Add-on (recurring monthly) */}
            {selectedPlan && Number(selectedPlan.leadGenAddonFee || 0) > 0 && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                <div>
                  <p className="text-sm text-white">Include Lead Generation Add-on</p>
                  <p className="text-xs text-(--color-muted-gray)">
                    ${Number(selectedPlan.leadGenAddonFee || 0)}/mo recurring
                    {billingCycle === 'YEARLY' && ' (billed ×12 on yearly)'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIncludeLeadGenAddon(!includeLeadGenAddon)}
                  className={cn(
                    'relative w-11 h-6 rounded-full transition-colors',
                    includeLeadGenAddon ? 'bg-(--color-signal-orange)' : 'bg-(--color-border-gray)',
                  )}
                >
                  <span
                    className={cn(
                      'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform',
                      includeLeadGenAddon && 'translate-x-5',
                    )}
                  />
                </button>
              </div>
            )}

            {/* Summary */}
            {selectedPlan && (
              <div className="p-4 rounded-lg bg-(--color-signal-orange)/5 border border-(--color-signal-orange)/20">
                <p className="text-xs font-semibold text-(--color-signal-orange) uppercase tracking-wider mb-3">
                  Payment Summary
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-(--color-muted-gray)">Plan</span>
                    <span className="text-white font-medium">{selectedPlan.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-(--color-muted-gray)">Billing</span>
                    <span className="text-white">{billingCycle === 'MONTHLY' ? 'Monthly' : 'Yearly'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-(--color-muted-gray)">Plan Amount</span>
                    <span className="text-white">${planAmount.toFixed(2)}</span>
                  </div>
                  {setupFeeAmount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-(--color-muted-gray)">Setup Fee</span>
                      <span className="text-white">${setupFeeAmount.toFixed(2)}</span>
                    </div>
                  )}
                  {leadGenAddonAmount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-(--color-muted-gray)">Lead Gen Add-on</span>
                      <span className="text-white">
                        ${leadGenAddonAmount.toFixed(2)}/{billingCycle === 'YEARLY' ? 'yr' : 'mo'}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-(--color-muted-gray)">Payment Via</span>
                    <span className="text-white">{PAYMENT_MODES.find(m => m.value === paymentMode)?.label}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-(--color-muted-gray)">Period</span>
                    <span className="text-white text-xs">
                      {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      {' → '}
                      {periodEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                  <div className="border-t border-(--color-signal-orange)/20 pt-2 mt-2 flex justify-between">
                    <span className="text-white font-semibold">Total Collected</span>
                    <span className="text-(--color-signal-orange) font-bold text-lg">${totalAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal Error */}
      {modalError && (
        <div className="mx-6 mb-4 flex items-center gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
          <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
          <p className="text-sm text-red-300 flex-1">{modalError}</p>
          <button onClick={() => setModalError(null)} className="text-red-400 hover:text-red-300">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-(--color-border-gray)">
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={!isFormValid || saving || loadingPlans}>
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Assigning...
            </>
          ) : (
            'Assign Plan'
          )}
        </Button>
      </div>
    </Modal>
  );
};

// ==================== ENABLE LEAD GEN ADD-ON MODAL ====================

interface EnableLeadGenModalProps {
  customer: CustomerOrganization | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Enables the Lead Generation add-on for a customer paying offline and records
 * the fee as a manual payment. Card customers enable it from their own Billing page.
 */
const EnableLeadGenModal = ({ customer, isOpen, onClose, onSuccess }: EnableLeadGenModalProps) => {
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [paymentMode, setPaymentMode] = useState<AssignPlanData['paymentMode']>('CASH');
  const [paymentReference, setPaymentReference] = useState('');
  const [notes, setNotes] = useState('');
  const [quote, setQuote] = useState<LeadGenAddonQuote | null>(null);
  const [loadingQuote, setLoadingQuote] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setPaymentMode('CASH');
      setPaymentReference('');
      setNotes('');
      setModalError(null);
      setQuote(null);
    }
  }, [isOpen]);

  // Exact amount to record: rest of the current period (prorated on yearly plans),
  // or nothing if the add-on is already paid for this period.
  useEffect(() => {
    if (!isOpen || !customer) return;
    setLoadingQuote(true);
    customersService.getLeadGenAddonQuote(customer.id)
      .then(response => {
        if (response.success && response.data) setQuote(response.data);
        else setModalError(response.message || 'Failed to load add-on pricing');
      })
      .finally(() => setLoadingQuote(false));
  }, [isOpen, customer]);

  const subscription = customer?.subscription;
  const cycleLabel = quote?.billingCycle === 'YEARLY' ? 'yr' : 'mo';

  const handleSave = async () => {
    if (!customer) return;

    try {
      setSaving(true);
      setModalError(null);

      const response = await customersService.setLeadGenAddon(customer.id, {
        enabled: true,
        paymentMode,
        ...(paymentReference.trim() && { paymentReference: paymentReference.trim() }),
        ...(notes.trim() && { notes: notes.trim() }),
      });
      if (response.success) {
        onClose();
        onSuccess();
      } else {
        setModalError(response.message || 'Failed to enable the add-on');
      }
    } catch (err: unknown) {
      setModalError(err instanceof Error ? err.message : 'Failed to enable the add-on');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Enable Lead Generation Add-on"
      subtitle={customer ? `${customer.user.firstName} ${customer.user.lastName} — ${customer.businessName}` : ''}
      size="lg"
      showCloseButton
    >
      <div className="space-y-5 p-6">
        <div className="flex items-start gap-3 p-4 rounded-lg bg-(--color-info)/10 border border-(--color-info)/30">
          <Info className="w-5 h-5 text-(--color-info) shrink-0 mt-0.5" />
          <div className="text-sm text-(--color-soft-gray)">
            <p>
              Records the add-on for the rest of the customer's current period as an offline payment and
              adds it to their renewal amount. The customer is emailed and their app updates right away.
            </p>
            {quote?.alreadyPaidThisPeriod && (
              <p className="mt-2 text-white">
                Already paid for this period (e.g. included when the plan was assigned), so no new payment
                will be recorded.
              </p>
            )}
          </div>
        </div>

        {/* Payment details - not needed when this period is already paid */}
        {!quote?.alreadyPaidThisPeriod && (
          <>
            {/* Payment Mode */}
            <div>
              <label className="block text-sm font-medium text-(--color-soft-gray) mb-2">
                Payment Method
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {PAYMENT_MODES.map(mode => (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() => setPaymentMode(mode.value)}
                    className={cn(
                      'py-2 px-3 rounded-lg border text-sm transition-all',
                      paymentMode === mode.value
                        ? 'border-(--color-signal-orange) bg-(--color-signal-orange)/10 text-(--color-signal-orange)'
                        : 'border-(--color-border-gray) bg-(--color-charcoal-black) text-(--color-soft-gray) hover:border-(--color-muted-gray)',
                    )}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Payment Reference */}
            <Input
              label="Payment Reference (optional)"
              placeholder="e.g., Cheque #1234, Receipt #5678"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
            />
          </>
        )}

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-(--color-soft-gray) mb-2">
            Notes (optional)
          </label>
          <textarea
            placeholder="Additional notes about this payment..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full px-4 py-3 rounded-lg bg-(--color-charcoal-black) border border-(--color-border-gray) text-white placeholder:text-(--color-muted-gray) focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 transition-all resize-none text-sm"
          />
        </div>

        {/* Summary */}
        {loadingQuote && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-6 h-6 animate-spin text-(--color-signal-orange)" />
          </div>
        )}
        {subscription && quote && (
          <div className="p-4 rounded-lg bg-(--color-signal-orange)/5 border border-(--color-signal-orange)/20">
            <p className="text-xs font-semibold text-(--color-signal-orange) uppercase tracking-wider mb-3">
              Payment Summary
            </p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-(--color-muted-gray)">Plan</span>
                <span className="text-white font-medium">
                  {subscription.plan.name} ({quote.billingCycle === 'YEARLY' ? 'Yearly' : 'Monthly'})
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-(--color-muted-gray)">Add-on Fee</span>
                <span className="text-white">${quote.monthlyFee.toFixed(2)}/mo</span>
              </div>
              {!quote.alreadyPaidThisPeriod && (
                <div className="flex justify-between">
                  <span className="text-(--color-muted-gray)">Payment Via</span>
                  <span className="text-white">{PAYMENT_MODES.find(m => m.value === paymentMode)?.label}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-(--color-muted-gray)">Active Until</span>
                <span className="text-white text-xs">
                  {new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-(--color-muted-gray)">Added to Renewal</span>
                <span className="text-white">${quote.recurringAmount.toFixed(2)}/{cycleLabel}</span>
              </div>
              <div className="border-t border-(--color-signal-orange)/20 pt-2 mt-2 flex justify-between">
                <span className="text-white font-semibold">
                  {quote.monthsCharged > 1 ? `Collected Now (${quote.monthsCharged} months)` : 'Collected Now'}
                </span>
                <span className="text-(--color-signal-orange) font-bold text-lg">${quote.chargeNow.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal Error */}
      {modalError && (
        <div className="mx-6 mb-4 flex items-center gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
          <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
          <p className="text-sm text-red-300 flex-1">{modalError}</p>
          <button onClick={() => setModalError(null)} className="text-red-400 hover:text-red-300">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-(--color-border-gray)">
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving || !subscription || !quote}>
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Enabling...
            </>
          ) : (
            'Enable Add-on'
          )}
        </Button>
      </div>
    </Modal>
  );
};

// ==================== MAIN COMPONENT ====================

const CustomersPage = () => {
  const [customers, setCustomers] = useState<CustomerOrganization[]>([]);
  const [stats, setStats] = useState<CustomerStatistics>({ total: 0, pending: 0, approved: 0, rejected: 0, suspended: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOrganization | null>(null);
  const [assignPlanCustomer, setAssignPlanCustomer] = useState<CustomerOrganization | null>(null);
  const [expirePlanCustomer, setExpirePlanCustomer] = useState<CustomerOrganization | null>(null);
  const [expiring, setExpiring] = useState(false);
  const [enableLeadGenCustomer, setEnableLeadGenCustomer] = useState<CustomerOrganization | null>(null);
  const [disableLeadGenCustomer, setDisableLeadGenCustomer] = useState<CustomerOrganization | null>(null);
  const [disablingLeadGen, setDisablingLeadGen] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(APP_CONSTANTS.DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Fetch customers
  const fetchCustomers = useCallback(async (page: number = currentPage, limit: number = pageSize) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const query: Parameters<typeof customersService.getAll>[0] = {
        page,
        limit,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      };
      
      if (searchQuery) {
        query.search = searchQuery;
      }
      
      if (filterStatus !== 'all') {
        query.status = filterStatus;
      }

      const response = await customersService.getAll(query);
      
      if (response.success && response.data) {
        // Handle paginated response (data.items) or direct array response
        const customersData = Array.isArray(response.data) 
          ? response.data 
          : (response.data.items || []);
        setCustomers(customersData);
        
        // Extract pagination meta
        if (!Array.isArray(response.data) && response.data.meta) {
          setTotalPages(response.data.meta.totalPages || 1);
          setTotalItems(response.data.meta.total || customersData.length);
        }
      } else {
        setError(response.message || 'Failed to load customers');
        setCustomers([]); // Reset to empty array on error
      }
    } catch {
      setError('Failed to load customers');
      setCustomers([]); // Reset to empty array on error
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, filterStatus, currentPage, pageSize]);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
      fetchCustomers(newPage, pageSize);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing page size
    fetchCustomers(1, newSize);
  };

  // Fetch statistics
  const fetchStats = useCallback(async () => {
    setIsStatsLoading(true);
    
    try {
      const response = await customersService.getStatistics();
      
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch {
      // Silent fail for stats
    } finally {
      setIsStatsLoading(false);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    fetchCustomers(currentPage, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
    fetchCustomers(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filterStatus]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Action handlers
  const handleApprove = async (id: string) => {
    setActionLoading(id);
    try {
      const response = await customersService.approve(id);
      if (response.success) {
        // Refresh data
        await Promise.all([fetchCustomers(), fetchStats()]);
      } else {
        setError(response.message || 'Failed to approve customer');
      }
    } catch {
      setError('Failed to approve customer');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id: string) => {
    setActionLoading(id);
    try {
      const response = await customersService.updateStatus(id, { 
        status: 'REJECTED',
        reason: 'Rejected by admin'
      });
      if (response.success) {
        await Promise.all([fetchCustomers(), fetchStats()]);
      } else {
        setError(response.message || 'Failed to reject customer');
      }
    } catch {
      setError('Failed to reject customer');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSuspend = async (id: string) => {
    setActionLoading(id);
    try {
      const response = await customersService.updateStatus(id, { 
        status: 'SUSPENDED',
        reason: 'Suspended by admin'
      });
      if (response.success) {
        await Promise.all([fetchCustomers(), fetchStats()]);
      } else {
        setError(response.message || 'Failed to suspend customer');
      }
    } catch {
      setError('Failed to suspend customer');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivate = async (id: string) => {
    setActionLoading(id);
    try {
      const response = await customersService.reactivate(id);
      if (response.success) {
        await Promise.all([fetchCustomers(), fetchStats()]);
      } else {
        setError(response.message || 'Failed to reactivate customer');
      }
    } catch {
      setError('Failed to reactivate customer');
    } finally {
      setActionLoading(null);
    }
  };

  const handleExpirePlan = async () => {
    if (!expirePlanCustomer) return;

    setExpiring(true);
    try {
      const response = await customersService.expireSubscription(expirePlanCustomer.id, {
        reason: 'Manually expired by admin',
      });
      if (response.success) {
        setExpirePlanCustomer(null);
        await Promise.all([fetchCustomers(), fetchStats()]);
      } else {
        setError(response.message || 'Failed to expire plan');
      }
    } catch {
      setError('Failed to expire plan');
    } finally {
      setExpiring(false);
    }
  };

  const handleDisableLeadGen = async () => {
    if (!disableLeadGenCustomer) return;

    setDisablingLeadGen(true);
    try {
      const response = await customersService.setLeadGenAddon(disableLeadGenCustomer.id, { enabled: false });
      if (response.success) {
        setDisableLeadGenCustomer(null);
        await fetchCustomers();
      } else {
        setError(response.message || 'Failed to disable the add-on');
      }
    } catch {
      setError('Failed to disable the add-on');
    } finally {
      setDisablingLeadGen(false);
    }
  };

  const handleView = (id: string) => {
    const customer = customers.find(c => c.id === id);
    if (customer) {
      setSelectedCustomer(customer);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getFilterLabel = (status: FilterStatus) => {
    switch (status) {
      case 'all': return 'All Status';
      case 'PENDING': return 'Pending';
      case 'APPROVED': return 'Active';
      case 'REJECTED': return 'Rejected';
      case 'SUSPENDED': return 'Suspended';
      default: return 'All Status';
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-6"
    >
      {/* Page Header */}
      <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white">Customers</h1>
          <p className="text-(--color-muted-gray) mt-1">
            Manage customer accounts and approvals
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => {
            fetchCustomers();
            fetchStats();
          }}
          leftIcon={<RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />}
          disabled={isLoading}
        >
          
        </Button>
      </motion.div>

      {/* Error Alert */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-xl bg-(--color-error)/10 border border-(--color-error)/30"
          >
            <AlertCircle size={20} className="text-(--color-error)" />
            <p className="text-sm text-(--color-error) flex-1">{error}</p>
            <Button variant="ghost" size="sm" onClick={() => setError(null)}>
              Dismiss
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stats Cards */}
      <motion.div variants={fadeInUp} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Customers"
          value={stats.total}
          icon={Users}
          color="bg-(--color-info)"
          isLoading={isStatsLoading}
        />
        <StatCard
          title="Pending Approval"
          value={stats.pending}
          icon={Clock}
          color="bg-(--color-warning)"
          isLoading={isStatsLoading}
        />
        <StatCard
          title="Active Customers"
          value={stats.approved}
          icon={CheckCircle}
          color="bg-(--color-success)"
          isLoading={isStatsLoading}
        />
        <StatCard
          title="Suspended"
          value={stats.suspended}
          icon={Ban}
          color="bg-(--color-muted-gray)"
          isLoading={isStatsLoading}
        />
      </motion.div>

      {/* Pending Approvals Alert */}
      {stats.pending > 0 && (
        <motion.div
          variants={fadeInUp}
          className="flex items-center gap-3 p-4 rounded-xl bg-(--color-warning)/10 border border-(--color-warning)/30"
        >
          <AlertCircle size={20} className="text-(--color-warning)" />
          <p className="text-sm text-(--color-warning)">
            You have <span className="font-semibold">{stats.pending}</span> customer(s) waiting for approval.
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFilterStatus('PENDING')}
            className="ml-auto text-(--color-warning)"
          >
            View Pending
          </Button>
        </motion.div>
      )}

      {/* Filters & Search */}
      <motion.div variants={fadeInUp}>
        <Card>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Search - Left Side */}
            <div className="w-full sm:w-80">
              <Input
                placeholder="Search customers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                leftIcon={<Search size={18} />}
              />
            </div>

            {/* Filter Dropdown - Right Side */}
            <div className="relative w-full sm:w-auto">
              <Button
                variant="secondary"
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                leftIcon={<Filter size={16} />}
                rightIcon={<ChevronDown size={14} />}
                className="w-full sm:w-auto justify-between sm:justify-center"
              >
                {getFilterLabel(filterStatus)}
              </Button>

              <AnimatePresence>
                {isFilterOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsFilterOpen(false)} />
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-full sm:w-44 py-1 bg-(--color-charcoal-black) border border-(--color-border-gray) rounded-lg shadow-xl z-50"
                    >
                      {(['all', 'PENDING', 'APPROVED', 'SUSPENDED', 'REJECTED'] as FilterStatus[]).map((status) => (
                        <button
                          key={status}
                          onClick={() => { setFilterStatus(status); setIsFilterOpen(false); }}
                          className={cn(
                            'w-full px-4 py-2.5 text-sm text-left transition-colors',
                            filterStatus === status
                              ? 'bg-(--color-signal-orange)/10 text-(--color-signal-orange)'
                              : 'text-(--color-soft-gray) hover:bg-white/5 hover:text-white'
                          )}
                        >
                          {getFilterLabel(status)}
                        </button>
                      ))}
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Customers Table */}
      <motion.div variants={fadeInUp}>
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-(--color-navy-light)/30">
                  <th className="px-5 py-3 text-left text-xs font-semibold text-(--color-muted-gray) uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-(--color-muted-gray) uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-(--color-muted-gray) uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-(--color-muted-gray) uppercase tracking-wider">
                    Subscription
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-(--color-muted-gray) uppercase tracking-wider">
                    Registered
                  </th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-(--color-muted-gray) uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-16 text-center">
                      <Loader2 size={28} className="text-(--color-signal-orange) mx-auto mb-3 animate-spin" />
                      <p className="text-sm text-(--color-muted-gray)">Loading customers...</p>
                    </td>
                  </tr>
                ) : !customers || customers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-16 text-center">
                      <div className="w-14 h-14 rounded-full bg-(--color-navy-light)/50 flex items-center justify-center mx-auto mb-3">
                        <Users size={24} className="text-(--color-muted-gray)" />
                      </div>
                      <p className="text-sm font-medium text-(--color-soft-gray) mb-1">No customers found</p>
                      <p className="text-xs text-(--color-muted-gray)">
                        {searchQuery || filterStatus !== 'all' 
                          ? 'Try adjusting your search or filters' 
                          : 'New customers will appear here'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  customers.map((customer) => (
                    <motion.tr
                      key={customer.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className={cn(
                        "border-b border-(--color-border-gray)/50 last:border-b-0 hover:bg-(--color-navy-light)/20 transition-colors",
                        actionLoading === customer.id && "opacity-50 pointer-events-none"
                      )}
                    >
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-linear-to-br from-(--color-signal-orange) to-(--color-warm-amber) flex items-center justify-center shrink-0 shadow-sm">
                            <span className="text-xs font-semibold text-white">
                              {customer.user.firstName[0]}{customer.user.lastName[0]}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-white truncate">
                              {customer.user.firstName} {customer.user.lastName}
                            </p>
                            <p className="text-xs text-(--color-muted-gray) flex items-center gap-1 truncate">
                              <Mail size={11} />
                              <span className="truncate">{customer.user.email}</span>
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <Building2 size={14} className="text-(--color-muted-gray) shrink-0" />
                          <span className="text-sm text-(--color-soft-gray) truncate max-w-[180px]">
                            {customer.businessName}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <StatusBadge status={customer.status} isUserActive={customer.user.isActive} />
                      </td>
                      <td className="px-5 py-3.5">
                        <SubscriptionBadge subscription={customer.subscription} />
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1.5 text-xs text-(--color-muted-gray)">
                          <Calendar size={13} />
                          {formatDate(customer.createdAt)}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {actionLoading === customer.id ? (
                            <Loader2 size={18} className="animate-spin text-(--color-signal-orange)" />
                          ) : (
                            <>
                              {(customer.status === 'PENDING' && customer.user.isActive) && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleApprove(customer.id)}
                                    className="text-(--color-success) hover:bg-(--color-success)/10 p-1.5"
                                  >
                                    <UserCheck size={16} />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleReject(customer.id)}
                                    className="text-(--color-error) hover:bg-(--color-error)/10 p-1.5"
                                  >
                                    <UserX size={16} />
                                  </Button>
                                </>
                              )}
                              <ActionDropdown
                                customer={customer}
                                onApprove={handleApprove}
                                onReject={handleReject}
                                onSuspend={handleSuspend}
                                onExpirePlan={setExpirePlanCustomer}
                                onReactivate={handleReactivate}
                                onView={handleView}
                                onAssignPlan={(c) => setAssignPlanCustomer(c)}
                                onEnableLeadGen={setEnableLeadGenCustomer}
                                onDisableLeadGen={setDisableLeadGenCustomer}
                              />
                            </>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            isLoading={isLoading}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            itemLabel="customers"
          />
        </Card>
      </motion.div>

      {/* Customer Details Modal */}
      <CustomerDetailsModal
        customer={selectedCustomer}
        isOpen={!!selectedCustomer}
        onClose={() => setSelectedCustomer(null)}
      />

      {/* Assign Plan Modal */}
      <AssignPlanModal
        customer={assignPlanCustomer}
        isOpen={!!assignPlanCustomer}
        onClose={() => setAssignPlanCustomer(null)}
        onSuccess={() => {
          fetchCustomers();
          fetchStats();
        }}
      />

      {/* Enable Lead Gen Add-on Modal */}
      <EnableLeadGenModal
        customer={enableLeadGenCustomer}
        isOpen={!!enableLeadGenCustomer}
        onClose={() => setEnableLeadGenCustomer(null)}
        onSuccess={() => {
          fetchCustomers();
        }}
      />

      {/* Disable Lead Gen Add-on Confirmation */}
      <ConfirmDialog
        isOpen={!!disableLeadGenCustomer}
        onClose={() => setDisableLeadGenCustomer(null)}
        onConfirm={handleDisableLeadGen}
        variant="warning"
        title="Disable Lead Generation add-on?"
        confirmText="Disable Add-on"
        isLoading={disablingLeadGen}
        description={
          <>
            <span className="block mb-2">
              <strong className="text-white">{disableLeadGenCustomer?.businessName}</strong> will
              immediately lose access to Lead Generation, their automated outreach stops, and the
              add-on is removed from their renewal amount.
            </span>
            <span className="block text-xs">
              No refund is recorded for the current period. The customer is notified by email.
            </span>
          </>
        }
      />

      {/* Expire Plan Confirmation */}
      <ConfirmDialog
        isOpen={!!expirePlanCustomer}
        onClose={() => setExpirePlanCustomer(null)}
        onConfirm={handleExpirePlan}
        variant="warning"
        title="Expire this plan?"
        confirmText="Expire Plan"
        isLoading={expiring}
        description={
          <>
            <span className="block mb-2">
              <strong className="text-white">{expirePlanCustomer?.businessName}</strong> will
              immediately lose access to all messaging services:
            </span>
            <span className="block text-xs leading-relaxed">
              • WhatsApp sending and inbound messages
              <br />• Running campaigns (paused within seconds)
              <br />• Keyword bot auto-replies
              <br />• SMS sending
              <br />• Contacts and templates
            </span>
            <span className="block mt-2 text-xs">
              They can still sign in and view their dashboard. Restore access with
              <strong className="text-white"> Assign Plan</strong>, or let them subscribe again.
            </span>
          </>
        }
      />
    </motion.div>
  );
};

export { CustomersPage };
